import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { X, Send } from "lucide-react";
import { showAlert, showErrorAlert } from "../../../components/HelperAlerts/HelpersAlerts.jsx";
import { getThemeContext } from "../../../components/providers/ThemeProvider.jsx";
import { VACIADO_COLOR } from "../colorPalette.js";
import nfcGuide from "../../../content/nfc-guide.json";

function extractSvgColors(svgString) {
    if (!svgString) return [];
    const matches = [...svgString.matchAll(/fill:(#[0-9a-fA-F]{6})/gi)];
    return [...new Set(matches.map((m) => m[1].toLowerCase()))];
}

function hasNfcInsert(svgString) {
    return /id=["'][^"']*insert_\d+_nfc_/i.test(svgString || "");
}

const NFC_VALUE_HINTS = {
    contact: "Teléfono, enlace de WhatsApp o dato de contacto",
    location: "Dirección o enlace de ubicación",
    other_url: "URL o dato que quieres guardar",
};

function hexToRgb(hex) {
    const clean = (hex || "").replace("#", "");
    if (clean.length !== 6) return null;
    return {
        r: parseInt(clean.slice(0, 2), 16),
        g: parseInt(clean.slice(2, 4), 16),
        b: parseInt(clean.slice(4, 6), 16),
    };
}

// MVP-07: sustituye la PALETTE estática por las referencias de filamento
// activas que devuelve el backend (`/filaments/active/`). El backend es la
// única fuente de disponibilidad real; este matching por distancia RGB solo
// decide qué referencia sugerir por defecto, nunca cuál se acepta.
function nearestReference(hex, references) {
    const target = hexToRgb(hex);
    if (!target || references.length === 0) return null;
    let best = null;
    let bestDistance = Infinity;
    for (const ref of references) {
        const rgb = hexToRgb(ref.color_hex);
        if (!rgb) continue;
        const distance = (rgb.r - target.r) ** 2 + (rgb.g - target.g) ** 2 + (rgb.b - target.b) ** 2;
        if (distance < bestDistance) {
            bestDistance = distance;
            best = ref;
        }
    }
    return best;
}

// Los colores del pedido vienen dados por el propio diseño: se mapean a la
// referencia activa más cercana y se agrupan. El vaciado (#000000) es
// grabado, no un color de impresión.
function buildPrintColors(svgColors, references) {
    const byReference = new Map();
    for (const hex of svgColors) {
        if (hex === VACIADO_COLOR) continue;
        const mapped = nearestReference(hex, references);
        if (!mapped) continue;
        const key = mapped.id;
        if (!byReference.has(key)) {
            byReference.set(key, { ...mapped, adjustedFrom: [] });
        }
        if (hex !== mapped.color_hex.toLowerCase()) {
            byReference.get(key).adjustedFrom.push(hex);
        }
    }
    return [...byReference.values()];
}

const INITIAL_FORM = {
    quantity: 1,
    notes: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    company_name: "",
};

export default function CustomOrderRequestModal({ keyring, onClose }) {
    const theme = getThemeContext();
    const svgColors = useMemo(() => extractSvgColors(keyring?.svg_2d), [keyring?.svg_2d]);
    const hasVaciado = svgColors.includes(VACIADO_COLOR);
    const hasNfc = useMemo(() => hasNfcInsert(keyring?.svg_2d), [keyring?.svg_2d]);

    const [references, setReferences] = useState([]);
    const [referencesLoading, setReferencesLoading] = useState(true);
    const [referencesError, setReferencesError] = useState("");
    const [filamentReferenceId, setFilamentReferenceId] = useState("");
    const [form, setForm] = useState({ ...INITIAL_FORM });
    const [nfcFunction, setNfcFunction] = useState("");
    const [nfcValue, setNfcValue] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [prefilled, setPrefilled] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setReferencesLoading(true);
        axios.get("/filaments/active/")
            .then(({ data }) => { if (!cancelled) setReferences(Array.isArray(data) ? data : []); })
            .catch((err) => {
                console.error("Error fetching active filament references:", err);
                if (!cancelled) setReferencesError("No se pudieron cargar los colores disponibles.");
            })
            .finally(() => { if (!cancelled) setReferencesLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const printColors = useMemo(
        () => buildPrintColors(svgColors, references),
        [svgColors, references],
    );
    const hasAdjustedColors = printColors.some((c) => c.adjustedFrom.length > 0);

    // Sugerencia por defecto: la referencia del primer color detectado en el
    // diseño. El cliente elige el color/acabado principal del lote — un
    // diseño con varios colores impresos sigue registrando ese detalle en
    // "Notas" hasta que exista una ampliación multi-referencia (ver docs).
    useEffect(() => {
        if (filamentReferenceId || printColors.length === 0) return;
        setFilamentReferenceId(String(printColors[0].id));
    }, [printColors, filamentReferenceId]);

    // Precarga de contacto desde el perfil del usuario y, si existe, su cliente
    // asociado. Solo rellena campos vacíos: lo que el usuario escriba manda, y
    // el snapshot que guarda CustomOrderRequest es lo que haya al enviar.
    useEffect(() => {
        let cancelled = false;

        const fillEmpty = (values) => {
            const usable = Object.entries(values).filter(([, v]) => v);
            if (!usable.length) return;
            setPrefilled(true);
            setForm((prev) => {
                let changed = false;
                const next = { ...prev };
                for (const [key, value] of usable) {
                    if (!next[key]) {
                        next[key] = value;
                        changed = true;
                    }
                }
                return changed ? next : prev;
            });
        };

        axios.get("/data_profile_user/")
            .then(({ data }) => {
                if (cancelled) return;
                fillEmpty({
                    contact_name: data?.name,
                    contact_email: data?.email,
                    contact_phone: data?.phone,
                });
            })
            .catch(() => { /* precarga no crítica */ });

        axios.get("/clients/me/")
            .then(({ data }) => {
                if (cancelled) return;
                fillEmpty({
                    company_name: data?.company_name,
                    contact_name: [data?.nombre, data?.apellido].filter(Boolean).join(" "),
                    contact_email: data?.email,
                    contact_phone: data?.telefonos?.[0]?.numero,
                });
            })
            .catch(() => { /* la mayoría de usuarios no tiene cliente asociado */ });

        return () => { cancelled = true; };
    }, []);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!keyring?.id || submitting) return;

        if (!filamentReferenceId) {
            await showErrorAlert({ text: "Selecciona un color/referencia de filamento disponible.", theme });
            return;
        }

        setSubmitting(true);
        try {
            await axios.post("/custom-order-requests/", {
                ...form,
                quantity: Number(form.quantity),
                customized_keyring: keyring.id,
                filament_reference: Number(filamentReferenceId),
                ...(hasNfc && (nfcFunction || nfcValue)
                    ? { nfc_config: { function: nfcFunction, value: nfcValue } }
                    : {}),
            });
            await showAlert({
                title: "Solicitud enviada",
                text: "Hemos recibido tu solicitud y la revisaremos manualmente.",
                icon: "success",
                theme,
            });
            onClose();
        } catch (error) {
            console.error("Error creating custom order request:", error);
            const detail = error.response?.data?.detail
                || error.response?.data?.filament_reference?.[0]
                || error.response?.data?.quantity?.[0]
                || error.response?.data?.nfc_config?.value?.[0]
                || error.response?.data?.nfc_config?.function?.[0]
                || error.response?.data?.nfc_config?.[0];
            await showErrorAlert({
                text: detail || "No se pudo enviar la solicitud.",
                theme,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const selectedReference = references.find((r) => String(r.id) === String(filamentReferenceId));

    return (
        createPortal(
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-4 py-4">
            <form
                onSubmit={handleSubmit}
                className="glassCard w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-lg p-6 shadow-xl"
            >
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                        <h2 className="text-xl font-semibold">Pedir este diseño</h2>
                        <p className="text-sm text-base-content/60">
                            {keyring?.name || "Diseño personalizado"}
                        </p>
                    </div>
                    <button type="button" className="btn btn-ghost btn-sm btn-circle" onClick={onClose}>
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="form-control">
                        <span className="label-text">Cantidad</span>
                        <input
                            className="input input-bordered"
                            type="number"
                            name="quantity"
                            min="1"
                            value={form.quantity}
                            onChange={handleChange}
                            required
                        />
                        <span className="text-xs text-base-content/50 mt-1">
                            Desde 1 unidad. Cualquier lote se revisa manualmente antes de aprobarse: la cantidad
                            indicada no es una capacidad ni un plazo garantizados.
                        </span>
                    </label>

                    <label className="form-control">
                        <span className="label-text">Material</span>
                        <input
                            className="input input-bordered"
                            value="PLA"
                            readOnly
                            disabled
                        />
                        <span className="text-xs text-base-content/50 mt-1">
                            De momento imprimimos todo en PLA.
                        </span>
                    </label>

                    <div className="form-control md:col-span-2">
                        <span className="label-text mb-1 block">Color / referencia de filamento</span>
                        {referencesLoading ? (
                            <div className="flex items-center gap-2 text-sm text-base-content/60">
                                <span className="loading loading-spinner loading-xs" /> Cargando colores disponibles…
                            </div>
                        ) : referencesError ? (
                            <div role="alert" className="alert alert-warning text-sm">{referencesError}</div>
                        ) : references.length === 0 ? (
                            <div role="alert" className="alert alert-warning text-sm">
                                No hay referencias de filamento activas en este momento.
                            </div>
                        ) : (
                            <>
                                {printColors.length > 0 && (
                                    <p className="text-xs text-base-content/50 mb-1.5">
                                        Detectado desde tu diseño{hasAdjustedColors ? ", ajustado a la referencia disponible más parecida" : ""}.
                                        {hasVaciado && " Incluye zonas de vaciado/grabado (no son color de impresión)."}
                                    </p>
                                )}
                                <div className="flex flex-wrap gap-2">
                                    {references.map((ref) => (
                                        <label
                                            key={ref.id}
                                            className={`flex items-center gap-1.5 rounded-full border pl-1 pr-2.5 py-1 text-xs cursor-pointer transition-colors ${
                                                String(filamentReferenceId) === String(ref.id)
                                                    ? "border-primary ring-2 ring-primary/40"
                                                    : "border-base-300"
                                            }`}
                                            title={`${ref.provider} · ${ref.display_name} · ${ref.finish}`}
                                        >
                                            <input
                                                type="radio"
                                                name="filament_reference"
                                                className="hidden"
                                                value={ref.id}
                                                checked={String(filamentReferenceId) === String(ref.id)}
                                                onChange={() => setFilamentReferenceId(String(ref.id))}
                                            />
                                            <span
                                                className="w-5 h-5 rounded-full border border-base-300"
                                                style={{ backgroundColor: ref.color_hex }}
                                            />
                                            {ref.display_name}
                                        </label>
                                    ))}
                                </div>
                                {selectedReference && (
                                    <p className="text-xs text-base-content/50 mt-1.5">
                                        Acabado: {selectedReference.finish} · {selectedReference.provider}
                                        {selectedReference.range_name ? ` ${selectedReference.range_name}` : ""}
                                    </p>
                                )}
                            </>
                        )}
                    </div>

                    {hasNfc && (
                        <fieldset className="md:col-span-2 rounded-xl border border-primary/25 bg-primary/5 p-4">
                            <legend className="px-1 text-base font-semibold">Contenido del NFC <span className="font-normal text-base-content/55">(opcional)</span></legend>
                            <p className="mb-3 text-sm text-base-content/70">
                                El chip se programa antes de fabricar. <a href="/guia-nfc" target="_blank" rel="noreferrer" className="link link-primary">¿Qué es el NFC? Ver guía</a>
                            </p>
                            <div className="grid gap-3 md:grid-cols-2">
                                <label className="form-control">
                                    <span className="label-text">Qué quieres que haga</span>
                                    <select className="select select-bordered" value={nfcFunction} onChange={(event) => setNfcFunction(event.target.value)}>
                                        <option value="">Lo decidiré más adelante</option>
                                        {nfcGuide.functions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                                    </select>
                                </label>
                                <label className="form-control">
                                    <span className="label-text">URL o dato</span>
                                    <input
                                        className="input input-bordered"
                                        value={nfcValue}
                                        onChange={(event) => setNfcValue(event.target.value)}
                                        placeholder={NFC_VALUE_HINTS[nfcFunction] || "https://tudominio.es/nfc"}
                                    />
                                </label>
                            </div>
                            <p className="mt-2 text-xs text-base-content/55">Si aún no lo tienes decidido, te lo pediremos antes de fabricar.</p>
                        </fieldset>
                    )}

                    <div className="md:col-span-2 mt-1">
                        <span className="label-text font-medium">Datos de contacto</span>
                        {prefilled && (
                            <p className="text-xs text-base-content/60 mt-0.5">
                                Rellenados desde tu perfil — puedes editarlos para esta solicitud.
                            </p>
                        )}
                    </div>

                    <label className="form-control">
                        <span className="label-text">Empresa opcional</span>
                        <input
                            className="input input-bordered"
                            name="company_name"
                            value={form.company_name}
                            onChange={handleChange}
                        />
                    </label>

                    <label className="form-control">
                        <span className="label-text">Nombre</span>
                        <input
                            className="input input-bordered"
                            name="contact_name"
                            value={form.contact_name}
                            onChange={handleChange}
                            required
                        />
                    </label>

                    <label className="form-control">
                        <span className="label-text">Email</span>
                        <input
                            className="input input-bordered"
                            type="email"
                            name="contact_email"
                            value={form.contact_email}
                            onChange={handleChange}
                            required
                        />
                    </label>

                    <label className="form-control md:col-span-2">
                        <span className="label-text">Teléfono opcional</span>
                        <input
                            className="input input-bordered"
                            name="contact_phone"
                            value={form.contact_phone}
                            onChange={handleChange}
                        />
                    </label>

                    <label className="form-control md:col-span-2">
                        <span className="label-text">Notas</span>
                        <textarea
                            className="textarea textarea-bordered min-h-24"
                            name="notes"
                            value={form.notes}
                            onChange={handleChange}
                            placeholder="Cuéntanos cualquier detalle importante para revisar el pedido (por ejemplo, si el diseño usa más de un color de impresión)."
                        />
                    </label>
                </div>

                <div className="mt-5 flex justify-end gap-2">
                    <button type="button" className="btn btn-ghost" onClick={onClose}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary gap-2"
                        disabled={submitting || referencesLoading || references.length === 0}
                    >
                        {submitting ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <Send className="w-4 h-4" />
                        )}
                        Enviar solicitud
                    </button>
                </div>
            </form>
        </div>,
        document.body,
        )
    );
}
